import { Link, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import ShareButton from './ShareButton.jsx'
import { useAuth } from '../hooks/useAuth'

const ENABLE_SEARCH = import.meta.env.VITE_ENABLE_SEARCH !== 'false'

export default function Navigation() {
    const { user, logout, isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const [showUserMenu, setShowUserMenu] = useState(false)
    const menuRef = useRef(null)

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false)
            }
        }

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showUserMenu])

    async function handleLogout() {
        try {
            await logout()
            setShowUserMenu(false)
            navigate('/')
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }

    return (
        <nav className="bg-logo-pink text-white">
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5">
                <Link to="/" className="font-logo text-3xl tracking-wide transition hover:opacity-90">
                    Make Merry
                </Link>
                <div className="flex items-center gap-4">
                    {ENABLE_SEARCH && (
                        <>
                            {isAuthenticated ? (
                                // Logged in - User menu dropdown
                                <div className="relative" ref={menuRef}>
                                    <button
                                        onClick={() => setShowUserMenu(!showUserMenu)}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                                    >
                                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">
                                            {user?.email?.[0]?.toUpperCase() || 'U'}
                                        </div>
                                        <span className="hidden sm:inline text-sm">
                                            {user?.email?.split('@')[0] || 'User'}
                                        </span>
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>

                                    {/* Dropdown menu */}
                                    {showUserMenu && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200 text-gray-900">
                                            <div className="px-4 py-3 border-b border-gray-200">
                                                <p className="text-xs text-gray-500">Signed in as</p>
                                                <p className="text-sm font-medium truncate">
                                                    {user?.email}
                                                </p>
                                            </div>
                                            <Link
                                                to="/account"
                                                onClick={() => setShowUserMenu(false)}
                                                className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                Account Settings
                                            </Link>
                                            <button
                                                onClick={handleLogout}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                </svg>
                                                Sign out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // Logged out state
                                <>
                                    <Link
                                        to="/login"
                                        className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
                                    >
                                        Sign in
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="px-4 py-2 rounded-lg bg-white text-logo-pink text-sm font-medium hover:bg-white/90 transition-colors"
                                    >
                                        Sign up
                                    </Link>
                                </>
                            )}
                        </>
                    )}
                    <ShareButton />
                </div>
            </div>
        </nav>
    )
}
